import {
  type ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import { Pressable } from './Pressable'
import { TextInput } from './TextInput'
import { WorkspaceBrandChip } from './WorkspaceBrandChip'

export type WorkspaceSwitcherRowProps = {
  id: string
  name: string
  isActive?: boolean
  logoUrl?: string | null
  accentColor?: string | null
  icon?: ReactNode
  subtitle?: ReactNode
  badge?: ReactNode
  action?: ReactNode
  className?: string
  onClick: () => void
}

export function WorkspaceSwitcherRow({
  name,
  isActive = false,
  logoUrl,
  accentColor,
  icon,
  subtitle,
  badge,
  action,
  className = '',
  onClick,
}: WorkspaceSwitcherRowProps) {
  return (
    <div
      className={`flex items-center rounded-xl transition ${
        isActive ? 'bg-[var(--surface-hover)] text-[var(--text-strong)]' : 'text-[var(--text)] hover:bg-[var(--surface-hover)]'
      } ${className}`}
    >
      <Pressable
        role="option"
        aria-selected={isActive}
        onClick={onClick}
        className="flex items-center gap-3 px-3 h-12 text-left flex-1 min-w-0"
      >
        {icon ? (
          icon
        ) : (
          <WorkspaceBrandChip
            name={name}
            logoUrl={logoUrl}
            accentColor={accentColor}
            fallback="none"
            size={28}
          />
        )}
        <div className="flex-1 min-w-0">
          <span className="block text-sm font-medium text-[var(--text-strong)] truncate">{name}</span>
          {subtitle ? (
            <span className="block text-[11px] text-[var(--muted)] truncate">{subtitle}</span>
          ) : null}
        </div>
        {badge}
        {isActive ? (
          <span className="text-[var(--success)] text-base font-bold shrink-0" aria-hidden="true" /* mosaic-keep: icon */>
            <CheckRoundedIcon fontSize="inherit" />
          </span>
        ) : null}
      </Pressable>
      {action}
    </div>
  )
}

export type WorkspaceSwitcherDropdownProps = {
  triggerLabel: string
  triggerBrandName?: string
  triggerLogoUrl?: string | null
  triggerAccentColor?: string | null
  triggerIcon?: ReactNode
  triggerMaxWidth?: string
  triggerTestId?: string
  dropdownWidth?: string
  testId?: string
  listTestId?: string
  dialogLabel?: string
  listboxLabel?: string
  closeBackdropLabel?: string
  open?: boolean
  onOpenChange?: (open: boolean) => void
  searchable?: boolean
  searchPlaceholder?: string
  noMatchText?: string
  emptyText?: string
  itemCount?: number
  footer?: ReactNode
  children: ReactNode | ((helpers: { search: string; close: () => void }) => ReactNode)
}

export function WorkspaceSwitcherDropdown({
  triggerLabel,
  triggerBrandName,
  triggerLogoUrl,
  triggerAccentColor,
  triggerIcon,
  triggerMaxWidth = 'max-w-[240px]',
  triggerTestId,
  dropdownWidth = 'w-[300px]',
  testId,
  listTestId,
  dialogLabel = 'Workspace picker',
  listboxLabel = 'Workspaces',
  closeBackdropLabel = 'Close workspace menu',
  open: controlledOpen,
  onOpenChange,
  searchable = true,
  searchPlaceholder = 'Search workspaces',
  itemCount = 0,
  footer,
  children,
}: WorkspaceSwitcherDropdownProps) {
  const [internalOpen, setInternalOpen] = useState(false)
  const [search, setSearch] = useState('')

  const isControlled = controlledOpen !== undefined
  const isOpen = isControlled ? controlledOpen : internalOpen

  const handleOpenChange = useCallback((next: boolean) => {
    if (!isControlled) setInternalOpen(next)
    onOpenChange?.(next)
    if (!next) setSearch('')
  }, [isControlled, onOpenChange])

  const showSearch = searchable && itemCount > 5

  const renderedContent = useMemo(() => {
    if (typeof children === 'function') {
      return children({ search, close: () => handleOpenChange(false) })
    }
    return children
  }, [children, search, handleOpenChange])

  return (
    <div className="relative flex-1 min-w-0 flex justify-center" data-testid={testId}>
      <Pressable
        onClick={() => handleOpenChange(!isOpen)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-2xl bg-[var(--surface-card)] border border-[var(--border-subtle)] ${triggerMaxWidth}`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        data-testid={triggerTestId}
      >
        {triggerIcon ? (
          triggerIcon
        ) : (
          <WorkspaceBrandChip
            name={triggerBrandName ?? triggerLabel}
            logoUrl={triggerLogoUrl}
            accentColor={triggerAccentColor}
            fallback="none"
            size={24}
          />
        )}
        <span className="text-sm font-semibold text-[var(--text-strong)] truncate">{triggerLabel}</span>
        <span className="text-[var(--muted)] text-xs" aria-hidden="true">
          <ExpandMoreRoundedIcon fontSize="inherit" />
        </span>
      </Pressable>

      <AnimatePresence>
        {isOpen ? (
          <>
            <Pressable
              className="fixed inset-0 z-30 bg-[color-mix(in_srgb,var(--bg)_60%,transparent)]"
              onClick={() => handleOpenChange(false)}
              aria-label={closeBackdropLabel}
            />
            <motion.div
              role="dialog"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.18 }}
              className={`absolute top-full mt-2 z-40 ${dropdownWidth} max-w-[90vw] bg-[var(--surface-card)] border border-[var(--border-subtle)] rounded-2xl shadow-[var(--shadow-pop)] p-2 flex flex-col gap-1 max-h-[60vh] overflow-hidden`}
              aria-label={dialogLabel}
              data-testid={listTestId}
            >
              {showSearch ? (
                <TextInput
                  value={search}
                  onChange={setSearch}
                  placeholder={searchPlaceholder}
                  size="sm"
                />
              ) : null}
              <div
                className="overflow-y-auto flex flex-col gap-1"
                role="listbox"
                aria-label={listboxLabel}
              >
                {renderedContent}
              </div>
              {footer}
            </motion.div>
          </>
        ) : null}
      </AnimatePresence>
    </div>
  )
}
